import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, Button, Input, Modal, Form, Select, Space, Segmented, message } from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined,
  TeamOutlined, WarningOutlined, DollarOutlined,
} from "@ant-design/icons";
import { ListPage, FormCard, StatCard, T, cardStyle, inputStyle, labelStyle, grid2 } from "../../shared/ui/PageShell";
import DateRangeFilter from "../../shared/ui/DateRangeFilter";
import useTablePagination from "../../shared/ui/useTablePagination";
import { confirmDanger } from "../../shared/ui/confirm";

/**
 * Side-by-side reference: "Today" (how a page currently might be built,
 * copying patterns found across the app) vs "Standard" (the shared
 * PageShell / useTablePagination / confirmDanger primitives applied
 * consistently). Nothing here is wired to a real API — it's a decision
 * aid for picking one convention before touching live pages.
 */

const FAKE_ROWS = [
  { id: 1, name: "Ahmed Al-Rashid", phone: "0750 111 2233", balance: 450000 },
  { id: 2, name: "Velan Co.", phone: "0770 555 8844", balance: 0 },
  { id: 3, name: "Sara Hassan", phone: "0780 323 9900", balance: 120000 },
];

/* ── Section wrapper: labels which side is "today" vs "standard" ───────── */
function Compare({ title, note, today, standard }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: T.text, marginBottom: 4 }}>{title}</div>
      {note && <div style={{ fontSize: 12, color: T.sub, marginBottom: 12, maxWidth: 720 }}>{note}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: T.red, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            Today (found in app)
          </div>
          {today}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: T.green, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
            Proposed standard
          </div>
          {standard}
        </div>
      </div>
    </div>
  );
}

/* ── 1. Table empty state ───────────────────────────────────────────────── */
function EmptyStateDemo() {
  const { t } = useTranslation();
  return (
    <Compare
      title="1. Table empty state"
      note="26 list pages (Users, Products, Vendors, Units, Categories, Roles, Customers…) never set locale.emptyText, so an empty table falls back to AntD's English 'No data' inside a Kurdish/Arabic app."
      today={
        <div style={cardStyle}>
          <Table size="small" dataSource={[]} pagination={false} columns={[{ title: "Name", dataIndex: "name" }, { title: "Phone", dataIndex: "phone" }]} />
        </div>
      }
      standard={
        <div style={cardStyle}>
          <Table
            size="small"
            dataSource={[]}
            pagination={false}
            columns={[{ title: "Name", dataIndex: "name" }, { title: "Phone", dataIndex: "phone" }]}
            locale={{ emptyText: t("noDataFound", "No data found") }}
          />
        </div>
      }
    />
  );
}

/* ── 2. Primary "Add" button ────────────────────────────────────────────── */
function AddButtonDemo() {
  return (
    <Compare
      title="2. Primary 'Add X' button"
      note="The top-right add button on list pages currently ships with three different corner radii depending on the file: default AntD (~6px), 9px, or 10px."
      today={
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Button type="primary" icon={<PlusOutlined />}>Default radius</Button>
          <Button type="primary" icon={<PlusOutlined />} style={{ borderRadius: 10, height: 40 }}>Radius 10</Button>
        </div>
      }
      standard={
        <Button type="primary" icon={<PlusOutlined />} style={{ borderRadius: 9, fontWeight: 700, height: 38, paddingInline: 18 }}>
          Add Customer
        </Button>
      }
    />
  );
}

/* ── 3. Modal-form input sizing ─────────────────────────────────────────── */
function ModalFormDemo() {
  const [openToday, setOpenToday] = useState(false);
  const [openStandard, setOpenStandard] = useState(false);
  return (
    <Compare
      title="3. Add/Edit forms inside a modal"
      note="Dedicated /add pages use FormPage's 42px-tall inputs (inputStyle), but quick-add modals (Vendors, Users, Customers, Products) skip that import and fall back to default 32px AntD inputs — the same 'add a record' action looks different depending on where it happens."
      today={
        <>
          <Button onClick={() => setOpenToday(true)} style={{ borderRadius: 9 }}>Open "today" modal</Button>
          <Modal title="Add Vendor" open={openToday} onCancel={() => setOpenToday(false)} onOk={() => setOpenToday(false)} okText="Save">
            <Form layout="vertical">
              <Form.Item label="Name"><Input placeholder="Vendor name" /></Form.Item>
              <Form.Item label="Phone"><Input placeholder="07xx xxx xxxx" /></Form.Item>
            </Form>
          </Modal>
        </>
      }
      standard={
        <>
          <Button onClick={() => setOpenStandard(true)} type="primary" style={{ borderRadius: 9 }}>Open standard modal</Button>
          <Modal title="Add Vendor" open={openStandard} onCancel={() => setOpenStandard(false)} onOk={() => setOpenStandard(false)} okText="Save">
            <Form layout="vertical">
              <Form.Item label={<span style={labelStyle}>Name</span>}><Input placeholder="Vendor name" style={inputStyle} /></Form.Item>
              <Form.Item label={<span style={labelStyle}>Phone</span>}><Input placeholder="07xx xxx xxxx" style={inputStyle} /></Form.Item>
            </Form>
          </Modal>
        </>
      }
    />
  );
}

/* ── 4. Confirm / delete dialog ─────────────────────────────────────────── */
function ConfirmDemo() {
  return (
    <Compare
      title="4. Delete confirmation"
      note="Three mechanisms exist today: imperative Modal.confirm written inline per-file, a controlled <Modal open={...}> pattern, and the shared confirmDanger() helper — each with slightly different title/okText wording."
      today={
        <Button
          danger
          style={{ borderRadius: 9 }}
          onClick={() => Modal.confirm({ title: "Delete this vendor? Only if no purchase orders exist.", okText: "Yes, Delete", onOk: () => message.success("Deleted") })}
        >
          Inline Modal.confirm
        </Button>
      }
      standard={
        <Button
          danger
          style={{ borderRadius: 9 }}
          onClick={() => confirmDanger({ title: "Delete this vendor?", content: "This action cannot be undone.", onOk: () => message.success("Deleted") })}
        >
          Shared confirmDanger()
        </Button>
      }
    />
  );
}

/* ── 5. Filter toolbar ──────────────────────────────────────────────────── */
function FilterToolbarDemo() {
  const [dateRange, setDateRange] = useState([null, null]);
  return (
    <Compare
      title="5. Filter / toolbar row"
      note="Report pages (FinancialReport, AccountantDashboard, TradingLinesReport) hand-roll their own filter bar in a bare <Card>, with different button radii and spacing than ListPage's shared toolbar convention used by Orders/Products/Cash/Expenses."
      today={
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 6, padding: 16, background: "#fff" }}>
          <Space wrap>
            <Input placeholder="Search…" style={{ width: 200 }} />
            <Button style={{ borderRadius: 10 }}>Filter</Button>
          </Space>
        </div>
      }
      standard={
        <div style={{ ...cardStyle, padding: "12px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Input.Search placeholder="Search here..." style={{ width: 260, borderRadius: 9 }} />
            <DateRangeFilter value={dateRange} onChange={setDateRange} showTime={false} size="small" />
          </div>
        </div>
      }
    />
  );
}

/* ── 6. Pagination ──────────────────────────────────────────────────────── */
function PaginationDemo() {
  const { pagination } = useTablePagination();
  return (
    <Compare
      title="6. Table pagination"
      note="useTablePagination keeps page/pageSize in real state so 'show 50 / page' sticks. Hardcoding pagination={{ pageSize: 10 }} as a fresh object literal every render (found in VendorView.js's consignment table) resets the page on every re-render."
      today={
        <div style={cardStyle}>
          <Table size="small" dataSource={FAKE_ROWS.map((r) => ({ ...r, key: r.id }))} columns={[{ title: "Name", dataIndex: "name" }]} pagination={{ pageSize: 10, size: "small" }} />
        </div>
      }
      standard={
        <div style={cardStyle}>
          <Table size="small" dataSource={FAKE_ROWS.map((r) => ({ ...r, key: r.id }))} columns={[{ title: "Name", dataIndex: "name" }]} pagination={{ ...pagination, size: "small" }} />
        </div>
      }
    />
  );
}

/* ── 7. StatCard color tokens ───────────────────────────────────────────── */
function StatCardDemo() {
  return (
    <Compare
      title="7. Stat card colors"
      note="iconBg pastels are duplicated as raw hex across ~13 files (#dbeafe, #dcfce7, #fee2e2, #fef3c7…) instead of named tokens — same visual result today, but proposing named tokens so the 'right' color is also the easy one to reach for."
      today={
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ ...cardStyle, padding: "14px 16px", minWidth: 150 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
              <TeamOutlined style={{ color: "#2563eb" }} />
            </div>
            <div style={{ fontWeight: 800, fontSize: 18 }}>128</div>
            <div style={{ fontSize: 12, color: T.muted }}>Customers</div>
          </div>
        </div>
      }
      standard={
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatCard icon={<TeamOutlined style={{ color: T.primary }} />} label="Customers" value={128} iconBg="#dbeafe" />
          <StatCard icon={<WarningOutlined style={{ color: T.red }} />} label="With debt" value={12} iconBg="#fee2e2" valueColor={T.red} />
          <StatCard icon={<DollarOutlined style={{ color: T.green }} />} label="Collected" value="4,250,000 IQD" iconBg="#dcfce7" valueColor={T.green} />
        </div>
      }
    />
  );
}

/* ── Root: a real ListPage using every "standard" primitive together ───── */
function FullPageExample() {
  const { pagination } = useTablePagination();
  const [modalOpen, setModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const columns = [
    { title: "#", key: "n", width: 44, render: (_, __, i) => <span style={{ color: T.muted }}>{i + 1}</span> },
    { title: <b>Name</b>, dataIndex: "name", key: "name" },
    { title: <b>Phone</b>, dataIndex: "phone", key: "phone" },
    {
      title: <b>Balance</b>, dataIndex: "balance", key: "balance", align: "right",
      render: (v) => v > 0
        ? <b style={{ color: T.red }}>{v.toLocaleString()} IQD</b>
        : <span style={{ color: T.green, fontWeight: 700 }}>Paid</span>,
    },
    {
      title: "", key: "actions", width: 90,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} style={{ borderRadius: 7 }} onClick={() => setModalOpen(true)} />
          <Button
            size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 7 }}
            onClick={() => confirmDanger({ title: `Delete ${r.name}?`, onOk: () => message.success("Deleted (demo only)") })}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <ListPage
        breadcrumb={["Design system", "Full page example"]}
        title="Customers"
        subtitle="Every proposed standard applied together on one real ListPage"
        action={
          <Button type="primary" icon={<PlusOutlined />} style={{ borderRadius: 9, fontWeight: 700, height: 38, paddingInline: 18 }} onClick={() => setModalOpen(true)}>
            Add Customer
          </Button>
        }
        stats={[
          <StatCard key="total" icon={<TeamOutlined style={{ color: T.primary }} />} label="Total customers" value={FAKE_ROWS.length} iconBg="#dbeafe" />,
          <StatCard key="debt" icon={<WarningOutlined style={{ color: T.red }} />} label="With debt" value={FAKE_ROWS.filter((r) => r.balance > 0).length} iconBg="#fee2e2" valueColor={T.red} />,
        ]}
        toolbar={
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <Input.Search placeholder="Search here..." style={{ width: 260, borderRadius: 9 }} />
            <Segmented value={statusFilter} onChange={setStatusFilter} options={[{ label: "All", value: "all" }, { label: "With debt", value: "debt" }]} />
          </div>
        }
      >
        <Table
          dataSource={FAKE_ROWS.map((r) => ({ ...r, key: r.id }))}
          columns={columns}
          pagination={{ ...pagination, size: "small" }}
          locale={{ emptyText: "No data found" }}
        />
      </ListPage>

      <Modal title="Add Customer" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={() => { setModalOpen(false); message.success("Saved (demo only)"); }} okText="Save">
        <Form layout="vertical">
          <div style={grid2}>
            <Form.Item label={<span style={labelStyle}>Full name</span>} rules={[{ required: true }]}>
              <Input prefix={<UserOutlined style={{ color: T.muted }} />} placeholder="Enter full name" style={inputStyle} />
            </Form.Item>
            <Form.Item label={<span style={labelStyle}>Type</span>}>
              <Select placeholder="Select type" style={{ height: 42 }} options={[{ value: "individual", label: "Individual" }, { value: "company", label: "Company" }]} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   MAIN
───────────────────────────────────────────────────────────────────────── */
export default function DesignSystemDemo() {
  const [view, setView] = useState("compare");

  return (
    <div style={{ padding: 24, background: T.bg, minHeight: "100%" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: "clamp(1.2rem, 3vw, 1.4rem)", fontWeight: 800, color: T.text }}>
          Design system — decide the standard
        </h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: T.sub, maxWidth: 720 }}>
          Nothing on this page is wired to real data. Each section below shows what's
          actually in the app today next to a proposed consistent version, based on an
          audit of tables, modals, filters, pagination, and forms across the codebase.
          Once you pick, the same primitives (ListPage, StatCard, useTablePagination,
          confirmDanger, inputStyle) get applied to the real pages.
        </p>
      </div>

      <div style={{ display: "inline-flex", gap: 4, background: "#fff", padding: 5, borderRadius: 11, border: `1px solid ${T.border}`, marginBottom: 24 }}>
        {[
          { key: "compare", label: "Side-by-side" },
          { key: "full", label: "Full page example" },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            style={{
              padding: "7px 18px", borderRadius: 7, border: "none", cursor: "pointer",
              fontWeight: 700, fontSize: 13,
              background: view === v.key ? T.primary : "transparent",
              color: view === v.key ? "#fff" : T.muted,
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === "compare" && (
        <FormCard title="Patterns" subtitle="Today vs proposed standard, one category at a time">
          <EmptyStateDemo />
          <AddButtonDemo />
          <ModalFormDemo />
          <ConfirmDemo />
          <FilterToolbarDemo />
          <PaginationDemo />
          <StatCardDemo />
        </FormCard>
      )}

      {view === "full" && <FullPageExample />}
    </div>
  );
}
